import type {
    EngagementReport,
    FleetEngagementReport,
    ProductEventBatch,
} from "../shared/interfaces";
import { authenticatedFetch } from "../util/authenticatedFetch";
import pRetry from "p-retry";

export type {
    EngagementReport,
    EngagementReportMember,
    FleetEngagementFeatureAdoption,
    FleetEngagementFeatureBreakdownAdoption,
    FleetEngagementReport,
    FleetEngagementTenantFeatureAdoption,
    FleetEngagementTenantOption,
    ProductEventBatch,
    ProductEventEnvelope,
} from "../shared/interfaces";

export class ProductEventTransportError extends Error {
    constructor(
        readonly status: number,
        readonly retryable: boolean,
        readonly retryAfterMs?: number,
    ) {
        super(`Product event ingestion failed (${status})`);
        this.name = "ProductEventTransportError";
    }
}

const MAX_RETRY_AFTER_MS = 60_000;
const MAX_RETRY_DELAY_MS = 8_000;

function retryAfterMilliseconds(value: string | null): number | undefined {
    if (!value) return undefined;
    const seconds = Number(value);
    const milliseconds = Number.isFinite(seconds)
        ? seconds * 1_000
        : Date.parse(value) - Date.now();
    if (!Number.isFinite(milliseconds) || milliseconds <= 0) return undefined;
    return Math.min(milliseconds, MAX_RETRY_AFTER_MS);
}

function wait(milliseconds: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, milliseconds));
}

function retryDelayMilliseconds(retryIndex: number): number {
    const backoff = Math.min(1_000 * 2 ** retryIndex, MAX_RETRY_DELAY_MS);
    return backoff * (1 + Math.random());
}

export async function postProductEvents(
    batch: ProductEventBatch,
    keepalive = false,
    send: typeof authenticatedFetch = authenticatedFetch,
): Promise<void> {
    await pRetry(
        async () => {
            const response = await send("/api/ai/events", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(batch),
                keepalive,
            });
            if (response.ok) return;

            const retryable =
                response.status === 408 || response.status === 429 || response.status >= 500;
            const error = new ProductEventTransportError(
                response.status,
                retryable,
                retryable ? retryAfterMilliseconds(response.headers.get("Retry-After")) : undefined,
            );
            if (!retryable) throw new pRetry.AbortError(error);
            throw error;
        },
        {
            retries: keepalive ? 1 : 3,
            // p-retry owns the attempt loop; this hook owns its single delay so
            // Retry-After and jittered backoff are combined rather than stacked.
            minTimeout: 0,
            maxTimeout: 0,
            onFailedAttempt: async (error) => {
                if (error.retriesLeft === 0) return;
                const retryAfter =
                    error instanceof ProductEventTransportError ? error.retryAfterMs : undefined;
                await wait(
                    Math.max(retryDelayMilliseconds(error.attemptNumber - 1), retryAfter ?? 0),
                );
            },
        },
    );
}

export async function getEngagementReport(
    days: number,
    includeInternal = false,
    weeks = 24,
): Promise<EngagementReport> {
    const query = new URLSearchParams({
        days: String(days),
        include_internal: String(includeInternal),
        weeks: String(weeks),
    });
    const response = await authenticatedFetch(`/api/ai/events/report?${query.toString()}`);
    if (!response.ok) {
        throw new Error(`Engagement report failed (${response.status})`);
    }
    return response.json() as Promise<EngagementReport>;
}

/**
 * The saved fleet report for the period, returned at once. `refresh` rebuilds it
 * now (a few seconds). The report always covers every tenant; scoping to a tenant
 * selection happens in the browser (`scopeFleetReport`).
 */
export async function getFleetEngagementReport(
    days: number,
    { refresh = false }: { refresh?: boolean } = {},
): Promise<FleetEngagementReport> {
    const query = new URLSearchParams({ days: String(days) });
    if (refresh) query.set("refresh", "1");
    const response = await authenticatedFetch(`/api/ai/events/fleet-report?${query.toString()}`);
    if (!response.ok) {
        throw new Error(`Fleet engagement report failed (${response.status})`);
    }
    return response.json() as Promise<FleetEngagementReport>;
}
